import clsx from 'clsx'
import { useMemo, type FC, type HTMLProps } from 'react'
import Image from 'next/image'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { capitalizeFirstLetter } from 'utils/text'
import { SUB_COMPETENCY_LIST } from 'app/PemetaanKompetensiPage/constants/competency'
import CompetencyStepper from '../ProfileRevamp/CompetencyStepper'

import type { TCompetency } from 'types/competency-profile'

interface SuccessDialogProps extends HTMLProps<HTMLDialogElement> {
  isLoading: boolean
  handleClose?: Function
  handleCTA: Function
  level: number
  title?: string
  CTAText?: string
  competency: TCompetency | string
}

const LEVEL_THRESHOLD = 2

interface ICompetencyColor {
  gradientFrom: string
  gradientTo: string
}

type TCompetencyColor = {
  [key in TCompetency]: ICompetencyColor
}

const CompetencyColors: TCompetencyColor = {
  pedagogik: {
    gradientFrom: 'from-[#C7F2E9]',
    gradientTo: 'to-[#6FBCAF]',
  },
  kepribadian: {
    gradientFrom: 'from-[#F9DEF9]',
    gradientTo: 'to-[#E879BE]',
  },
  sosial: {
    gradientFrom: 'from-[#EEE6F4]',
    gradientTo: 'to-[#AC93C6]',
  },
  profesional: {
    gradientFrom: 'from-[#CAF5FF]',
    gradientTo: 'to-[#5ABDE5]',
  },
}

const SuccessDialog: FC<SuccessDialogProps> = ({
  handleClose = () => {},
  isLoading,
  handleCTA,
  open,
  competency,
  level,
  title = 'Simpan draf sebelum keluar?',
}) => {
  const competencyColor: ICompetencyColor = useMemo(() => {
    const currentCompetencyLowercase = competency.toLowerCase()
    if (!SUB_COMPETENCY_LIST.includes(currentCompetencyLowercase)) {
      return {
        gradientFrom: 'from-[#C7F2E9]', // for default
        gradientTo: 'to-[#6FBCAF]', // for default
      }
    }
    return CompetencyColors[currentCompetencyLowercase]
  }, [competency])

  return (
    <Dialog
      isOpen={open}
      onClose={isLoading ? null : () => handleClose()}
      closeOnOverlayClick={false}
    >
      <style jsx global>{`
        .animated {
          -webkit-animation-duration: 1s;
          animation-duration: 1s;
          -webkit-animation-fill-mode: both;
          animation-fill-mode: both;
        }
        .animated.infinite {
          -webkit-animation-iteration-count: infinite;
          animation-iteration-count: infinite;
        }
        .animated.hinge {
          -webkit-animation-duration: 2s;
          animation-duration: 2s;
        }
        /*the animation definition*/
        @-webkit-keyframes tada {
          0% {
            -webkit-transform: scale3d(1, 1, 1);
            transform: scale3d(1, 1, 1);
          }
          10%,
          20% {
            -webkit-transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
            transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
          }
          30%,
          50%,
          70%,
          90% {
            -webkit-transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
          }
          40%,
          60%,
          80% {
            -webkit-transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
          }
          100% {
            -webkit-transform: scale3d(1, 1, 1);
            transform: scale3d(1, 1, 1);
          }
        }
        @keyframes tada {
          0% {
            -webkit-transform: scale3d(1, 1, 1);
            -ms-transform: scale3d(1, 1, 1);
            transform: scale3d(1, 1, 1);
          }
          10%,
          20% {
            -webkit-transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
            -ms-transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
            transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
          }
          30%,
          50%,
          70%,
          90% {
            -webkit-transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
            -ms-transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
          }
          40%,
          60%,
          80% {
            -webkit-transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
            -ms-transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
          }
          100% {
            -webkit-transform: scale3d(1, 1, 1);
            -ms-transform: scale3d(1, 1, 1);
            transform: scale3d(1, 1, 1);
          }
        }
        .tada {
          -webkit-animation-name: tada;
          animation-name: tada;
        }
      `}</style>
      <DialogOverlay />
      <DialogContent className="max-w-2xl m-auto self-center">
        <div
          className={clsx(
            '!space-y-[15px] rounded-t-lg text-center min-h-[218px] bg-gradient-to-b pt-[34px] px-[30px] pb-[25px]',
            competencyColor.gradientFrom,
            competencyColor.gradientTo
          )}
        >
          <Text variant="display-md">{capitalizeFirstLetter(competency)}</Text>
          <div className="relative aspect-[184/108] object-cover">
            <Image
              className="tada animated !px-[30px]"
              src={`/images/refleksi-kompetensi/badge/level-${level}.svg`}
              layout="fill"
              alt={`level ${level}`}
            />
          </div>
        </div>
        <div className="py-3 px-5">
          <CompetencyStepper step={level} animated />
        </div>
        <div className="p-4">
          <Text variant="heading-sm" className="mb-1">
            {title}
          </Text>

          <Text variant="body">
            {level > LEVEL_THRESHOLD
              ? 'Terima kasih sudah menyelesaikan semua pertanyaan di kompetensi ini!'
              : 'Jangan khawatir, level ini belum berdampak langsung ke pangkat/jenjang profesi dan Rapor Pendidikan. Terima kasih sudah berefleksi, ya.'}
          </Text>

          <Button
            className="mt-4 mb-2"
            color="blue"
            disabled={isLoading}
            isLoading={isLoading}
            onClick={() => handleCTA()}
            size="lg"
            fullWidth
          >
            Saya mengerti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SuccessDialog
